import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchEmails } from '../store/slices/emailsSlice';
import { useNavigation } from '../hooks/useNavigation';
import { formatDate } from '../utils/formatters';
import { FiAlertCircle, FiInbox } from 'react-icons/fi';
import ScreenContainer from '../components/ScreenContainer';

function EmailsScreen() {
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();

  const { emails, status, error, hasMore, currentPage } = useAppSelector((state) => state.emails);

  const isLoadingInitial = status === 'loading' && emails.length === 0;
  const isLoadingMore = status === 'loadingMore';

  useEffect(() => {
    dispatch(fetchEmails({ page: 0, forceRefresh: true }));
  }, [dispatch]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || status === 'failed') {
      return;
    }
    dispatch(fetchEmails({ page: currentPage + 1 }));
  }, [dispatch, hasMore, isLoadingMore, status, currentPage]);

  return (
    <ScreenContainer title="Emails">
      <div className="mx-auto flex min-h-full w-full max-w-lg flex-col pb-8">
        <div className="space-y-4 px-4 pt-2">
          {isLoadingInitial && (
            <div className="flex items-center justify-center py-10">
              <span className="h-5 w-5 animate-spin rounded-full border border-muted border-t-foreground" />
            </div>
          )}

          {!isLoadingInitial && error && emails.length === 0 && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900/40">
              <div className="flex items-start gap-3">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {!isLoadingInitial && !error && emails.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
              <FiInbox className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No emails found.</p>
            </div>
          )}

          {emails.length > 0 && (
            <div className="space-y-2">
              {emails.map((email) => (
                <button
                  type="button"
                  key={email.id}
                  onClick={() => navigateTo(`/emails/${email.id}`)}
                  className="group w-full text-left rounded-xl border border-border bg-secondary p-3 transition-colors"
                >
                  <div className="flex justify-between items-center mb-2 pointer-events-none min-w-0">
                    <h2 className="text-muted-foreground text-sm font-semibold flex-grow mr-2 truncate">
                      {email.subject || '(No Subject)'}
                    </h2>
                    <span className="text-muted-foreground text-xs font-semibold flex-shrink-0">
                      {formatDate(email.messageDateTime || email.receivedAt, true)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center mt-1 pointer-events-none min-w-0">
                    <div className="min-w-0">
                      <div className="text-sm text-foreground break-all [overflow-wrap:anywhere]">
                        <span className="text-muted-foreground">
                          {email.headerFrom || email.envelopeSender || 'Unknown sender'}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}

              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  className="w-full rounded-xl border border-border py-2.5 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </ScreenContainer>
  );
}

export default EmailsScreen;
